import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaArrowLeft, FaExclamationTriangle, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../../components/Reutilizables/ConfirmationModal';
import FetchWithGif from '../../../components/Reutilizables/FetchWithGif';
import {
  createAdminCompromisoPago,
  deleteAdminCompromisoPago,
  getAdminGestionMoras,
  getAdminGestionMorasAsesores,
  updateAdminCompromisoPago
} from '../../../js/adminGestionMorasService';
import CompromisoPagoModal from './CompromisoPagoModal';
import {
  CuotasVencidasTable,
  GestionMorasEmptyState,
  GestionMorasErrorState,
  GestionMorasMetricCards,
  GestionMorasTable,
  HistorialCompromisosCard,
  PrestamoAsesorCell,
  PrestamoClienteCell,
  PrestamoCurrencyCell,
  PrestamoNumeroCreditoCell
} from '../../../components/ui/GestionMorasComponents/GestionMorasShared';
import useCompromisoPagoForm from '../../../components/ui/GestionMorasComponents/useCompromisoPagoForm';
import {
  crearFormularioCompromiso,
  formatearFecha,
  obtenerFechaActualISO,
  validarCompromisoForm
} from '../../../utilities/gestionMorasUtils';

const INITIAL_FILTERS = { search: '', asesor_id: '' };
const INITIAL_MODAL_STATE = { mode: 'create', prestamo: null, compromiso: null };
const INITIAL_DELETE_CONFIRM_STATE = { show: false, prestamo: null, compromiso: null };
const PRESTAMO_HEADERS = [
  { label: 'Cliente', className: 'px-4 py-4 text-left' },
  { label: 'Asesor', className: 'px-4 py-4 text-left' },
  { label: 'N° Crédito', className: 'px-4 py-4 text-center' },
  { label: 'Capital pendiente', className: 'px-4 py-4 text-center' },
  { label: 'Total mora', className: 'px-4 py-4 text-center' },
  { label: 'Total a pagar', className: 'px-4 py-4 text-center' },
  { label: 'Compromisos', className: 'px-4 py-4 text-center' }
];

export default function GestionMorasUI() {
  const [prestamos, setPrestamos] = useState([]);
  const [meta, setMeta] = useState({});
  const [asesores, setAsesores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPrestamoId, setSelectedPrestamoId] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [activeFilters, setActiveFilters] = useState(INITIAL_FILTERS);
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);
  const [deleteConfirmState, setDeleteConfirmState] = useState(INITIAL_DELETE_CONFIRM_STATE);
  const [savingCompromiso, setSavingCompromiso] = useState(false);
  const [deletingCompromisoId, setDeletingCompromisoId] = useState(null);
  const {
    formData: compromisoForm,
    errors: compromisoErrors,
    setErrors: setCompromisoErrors,
    resetForm: resetCompromisoForm,
    handleChange: handleCompromisoChange
  } = useCompromisoPagoForm(crearFormularioCompromiso);

  const asesoresMap = useMemo(
    () =>
      asesores.reduce((acc, asesor) => {
        acc[String(asesor.id)] = asesor.nombre;
        return acc;
      }, {}),
    [asesores]
  );

  const aplicarGestionMoras = useCallback((data, selectedId = null) => {
    const nextPrestamos = Array.isArray(data.data) ? data.data : [];
    const nextSelectedId =
      selectedId !== null && nextPrestamos.some((prestamo) => prestamo.id_prestamo === selectedId)
        ? selectedId
        : null;

    setPrestamos(nextPrestamos);
    setMeta(data.meta || {});
    setSelectedPrestamoId(nextSelectedId);
  }, []);

  const cargarGestionMoras = useCallback(
    async (nextFilters = activeFilters, selectedId = null) => {
      try {
        setLoading(true);
        setError(null);

        const data = await getAdminGestionMoras(nextFilters);
        aplicarGestionMoras(data, selectedId);
      } catch (err) {
        console.error('Error al cargar la gestión de moras admin:', err);
        setError(err.message || 'No se pudo cargar la gestión de moras');
        toast.error(err.message || 'Error al cargar la gestión de moras');
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [activeFilters, aplicarGestionMoras]
  );

  const cargarDatosIniciales = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [asesoresData, gestionMorasData] = await Promise.all([
        getAdminGestionMorasAsesores(),
        getAdminGestionMoras(INITIAL_FILTERS)
      ]);

      setAsesores(asesoresData.asesores || []);
      aplicarGestionMoras(gestionMorasData);
    } catch (err) {
      console.error('Error al cargar datos iniciales de gestión de moras admin:', err);
      setError(err.message || 'No se pudo cargar la gestión de moras');
      toast.error(err.message || 'Error al cargar la gestión de moras');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [aplicarGestionMoras]);

  useEffect(() => {
    cargarDatosIniciales();
  }, [cargarDatosIniciales]);

  const handleSeleccionarPrestamo = useCallback((prestamoId) => {
    setSelectedPrestamoId(prestamoId);
  }, []);

  const handleVolverAlListado = useCallback(() => {
    setSelectedPrestamoId(null);
  }, []);

  const limpiarModalCompromiso = useCallback(() => {
    setModalState(INITIAL_MODAL_STATE);
    resetCompromisoForm();
  }, [resetCompromisoForm]);

  const abrirModalCrearCompromiso = useCallback(
    (prestamo) => {
      setModalState({ mode: 'create', prestamo, compromiso: null });
      resetCompromisoForm(prestamo);
    },
    [resetCompromisoForm]
  );

  const abrirModalEditarCompromiso = useCallback(
    (prestamo, compromiso) => {
      setModalState({ mode: 'edit', prestamo, compromiso });
      resetCompromisoForm(prestamo, compromiso);
    },
    [resetCompromisoForm]
  );

  const cerrarModalCompromiso = useCallback(() => {
    if (!savingCompromiso) {
      limpiarModalCompromiso();
    }
  }, [limpiarModalCompromiso, savingCompromiso]);

  const abrirConfirmacionEliminarCompromiso = useCallback((prestamo, compromiso) => {
    setDeleteConfirmState({ show: true, prestamo, compromiso });
  }, []);

  const cerrarConfirmacionEliminarCompromiso = useCallback(() => {
    if (deletingCompromisoId === null) {
      setDeleteConfirmState(INITIAL_DELETE_CONFIRM_STATE);
    }
  }, [deletingCompromisoId]);

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  }, []);

  const handleAplicarFiltros = useCallback(
    async (event) => {
      event.preventDefault();
      setActiveFilters(filters);
      setSelectedPrestamoId(null);
      await cargarGestionMoras(filters, null);
    },
    [cargarGestionMoras, filters]
  );

  const handleLimpiarFiltros = useCallback(async () => {
    setFilters(INITIAL_FILTERS);
    setActiveFilters(INITIAL_FILTERS);
    setSelectedPrestamoId(null);
    await cargarGestionMoras(INITIAL_FILTERS, null);
  }, [cargarGestionMoras]);

  const handleGuardarCompromiso = useCallback(
    async (event) => {
      event.preventDefault();

      const nextErrors = validarCompromisoForm(compromisoForm, {
        minFechaCompromiso: modalState.mode === 'create' ? obtenerFechaActualISO() : ''
      });
      if (Object.keys(nextErrors).length > 0) {
        setCompromisoErrors(nextErrors);
        return;
      }

      const payload = {
        fecha_compromiso: compromisoForm.fecha_compromiso,
        monto_compromiso: Number.parseFloat(compromisoForm.monto_compromiso),
        motivo: compromisoForm.motivo.trim()
      };

      try {
        setSavingCompromiso(true);
        setCompromisoErrors({});

        if (modalState.mode === 'edit' && compromisoForm.idCompromiso) {
          await updateAdminCompromisoPago(compromisoForm.idCompromiso, payload);
          toast.success('Compromiso de pago actualizado correctamente');
        } else {
          await createAdminCompromisoPago({ ...payload, idPrestamo: Number(compromisoForm.idPrestamo) });
          toast.success('Compromiso de pago registrado correctamente');
        }

        const selectedId = modalState.prestamo?.id_prestamo ?? selectedPrestamoId ?? null;
        limpiarModalCompromiso();
        await cargarGestionMoras(activeFilters, selectedId);
      } catch (err) {
        console.error('Error al guardar compromiso de pago admin:', err);
        toast.error(err.message || 'No se pudo guardar el compromiso de pago');
      } finally {
        setSavingCompromiso(false);
      }
    },
    [activeFilters, cargarGestionMoras, compromisoForm, limpiarModalCompromiso, modalState, selectedPrestamoId, setCompromisoErrors]
  );

  const handleEliminarCompromiso = useCallback(
    async () => {
      const prestamo = deleteConfirmState.prestamo;
      const compromiso = deleteConfirmState.compromiso;

      if (!compromiso?.id) {
        return;
      }

      try {
        setDeletingCompromisoId(compromiso.id);
        await deleteAdminCompromisoPago(compromiso.id);
        toast.success('Compromiso de pago eliminado correctamente');

        const selectedId = prestamo?.id_prestamo ?? selectedPrestamoId ?? null;
        await cargarGestionMoras(activeFilters, selectedId);
      } catch (err) {
        console.error('Error al eliminar compromiso de pago admin:', err);
        toast.error(err.message || 'No se pudo eliminar el compromiso de pago');
      } finally {
        setDeletingCompromisoId(null);
        setDeleteConfirmState(INITIAL_DELETE_CONFIRM_STATE);
      }
    },
    [activeFilters, cargarGestionMoras, deleteConfirmState, selectedPrestamoId]
  );

  const selectedPrestamo =
    selectedPrestamoId !== null
      ? prestamos.find((prestamo) => prestamo.id_prestamo === selectedPrestamoId) ?? null
      : null;

  const visiblePrestamos =
    selectedPrestamoId !== null
      ? prestamos.filter((prestamo) => prestamo.id_prestamo === selectedPrestamoId)
      : prestamos;
  const showFetchOverlay = savingCompromiso || deletingCompromisoId !== null || (loading && !initialLoad);

  if (loading && initialLoad) {
    return <FetchWithGif />;
  }

  return (
    <div className="space-y-6">
      {showFetchOverlay ? <FetchWithGif /> : null}
      <section className="ui-card px-6 py-7 md:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="mt-2 flex items-center gap-3 text-2xl font-semibold text-slate-900 md:text-3xl">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-600 shadow-sm">
                <FaExclamationTriangle className="h-6 w-6" />
              </span>
              Gestión de moras
            </h1>
            <p className="mt-3 max-w-3xl text-sm text-slate-600">
              Seguimiento global de créditos en mora, con historial completo de compromisos por préstamo.
            </p>
          </div>

          {activeFilters.search || activeFilters.asesor_id ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <p className="font-semibold">Filtros activos</p>
              <p className="mt-1">
                {activeFilters.search ? `Búsqueda: ${activeFilters.search}` : 'Búsqueda: todas'}
                {activeFilters.asesor_id
                  ? ` · Asesor: ${asesoresMap[String(activeFilters.asesor_id)] || activeFilters.asesor_id}`
                  : ' · Asesor: todos'}
              </p>
            </div>
          ) : null}
        </div>
      </section>

      <GestionMorasMetricCards meta={meta} />

      {selectedPrestamo ? (
        <section className="ui-card border border-red-200 bg-red-50/70 px-5 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-600">Detalle del préstamo</p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">
                Crédito #{selectedPrestamo.numero_credito} · {selectedPrestamo.cliente}
              </h2>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Mostrando 1 de {prestamos.length} préstamo{prestamos.length === 1 ? '' : 's'}
              </p>
            </div>

            <button
              type="button"
              onClick={handleVolverAlListado}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <FaArrowLeft className="h-3.5 w-3.5" />
              Volver al listado
            </button>
          </div>
        </section>
      ) : null}

      <section className="ui-card p-5">
        <form className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_0.8fr_auto_auto]" onSubmit={handleAplicarFiltros}>
          <div>
            <label htmlFor="search" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Buscar cliente o N° crédito
            </label>
            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                id="search"
                name="search"
                type="text"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Nombre del cliente o código del crédito"
                className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
              />
            </div>
          </div>

          <div>
            <label htmlFor="asesor_id" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Asesor
            </label>
            <select
              id="asesor_id"
              name="asesor_id"
              value={filters.asesor_id}
              onChange={handleFilterChange}
              className="w-full appearance-none rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
            >
              <option value="">Todos los asesores</option>
              {asesores.map((asesor) => (
                <option key={asesor.id} value={asesor.id}>
                  {asesor.nombre}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center rounded-2xl bg-red-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 lg:self-end"
          >
            {loading ? 'Aplicando...' : 'Aplicar filtros'}
          </button>

          <button
            type="button"
            onClick={handleLimpiarFiltros}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 lg:self-end"
          >
            Limpiar
          </button>
        </form>
      </section>

      <GestionMorasErrorState error={error} onRetry={() => cargarGestionMoras(activeFilters, selectedPrestamoId)} />

      {!error && !loading && prestamos.length === 0 ? (
        <GestionMorasEmptyState description="No se encontraron créditos en mora con los filtros seleccionados." />
      ) : null}

      {!error && visiblePrestamos.length > 0 ? (
        <GestionMorasTable
          headers={PRESTAMO_HEADERS}
          prestamos={visiblePrestamos}
          expandedPrestamoId={selectedPrestamoId}
          onToggleExpanded={handleSeleccionarPrestamo}
          rowInteractionDisabled={Boolean(selectedPrestamo)}
          detailRowIdPrefix="detalle-mora-admin"
          colSpan={7}
          renderRowCells={(prestamo) => {
            const compromisos = Array.isArray(prestamo.compromisos_pago) ? prestamo.compromisos_pago : [];

            return (
              <>
                <PrestamoClienteCell prestamo={prestamo} />
                <PrestamoAsesorCell prestamo={prestamo} />
                <PrestamoNumeroCreditoCell prestamo={prestamo} />
                <PrestamoCurrencyCell value={prestamo.capital_pendiente} />
                <PrestamoCurrencyCell value={prestamo.total_mora} className="text-slate-900" />
                <PrestamoCurrencyCell value={prestamo.total_a_pagar} className="bg-red-50 text-red-700" pill />
                <td className="px-4 py-4 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        abrirModalCrearCompromiso(prestamo);
                      }}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                      }}
                      className="inline-flex items-center rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-100"
                    >
                      Registrar compromiso
                    </button>

                    {compromisos.length > 0 ? (
                      <span className="text-xs text-slate-500">
                        {compromisos.length} registro{compromisos.length === 1 ? '' : 's'} · Último{' '}
                        {formatearFecha(compromisos[0].fecha_compromiso)}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Sin registros</span>
                    )}
                  </div>
                </td>
              </>
            );
          }}
          renderExpandedContent={(prestamo) => (
            <>
              <HistorialCompromisosCard
                compromisos={prestamo.compromisos_pago}
                onEdit={(compromiso) => abrirModalEditarCompromiso(prestamo, compromiso)}
                onDelete={(compromiso) => abrirConfirmacionEliminarCompromiso(prestamo, compromiso)}
                deletingCompromisoId={deletingCompromisoId}
                disableActions={savingCompromiso || deletingCompromisoId !== null}
              />
              <CuotasVencidasTable cuotas={prestamo.cuotas} title="Cuotas vencidas del préstamo" />
            </>
          )}
        />
      ) : null}

      <CompromisoPagoModal
        isOpen={Boolean(modalState.prestamo)}
        mode={modalState.mode}
        prestamo={modalState.prestamo}
        compromiso={modalState.compromiso}
        formData={compromisoForm}
        errors={compromisoErrors}
        saving={savingCompromiso}
        onClose={cerrarModalCompromiso}
        onChange={handleCompromisoChange}
        onSubmit={handleGuardarCompromiso}
      />

      <ConfirmationModal
        show={deleteConfirmState.show}
        title="¿Confirmar eliminación del compromiso?"
        message="¿Está seguro de eliminar este compromiso de pago? Esta acción no se puede deshacer."
        onClose={cerrarConfirmacionEliminarCompromiso}
        onConfirm={handleEliminarCompromiso}
        confirmText="Sí, eliminar"
        cancelText="No, cancelar"
      />
    </div>
  );
}